import { BaseTelemetryPlugin, IProcessTelemetryContext, ITelemetryItem } from '@microsoft/applicationinsights-web';

const REDACTED = '{detailId}';
const DOCUMENT_ROUTE_SEGMENTS = new Set(['view-document']);
const HASH_QUERY_PARAM = /([?&]hash=)[A-Za-z0-9\-._~%]+/gi;
const REDACTED_SIGNATURE = '{sig}';
const SAS_SIGNATURE_QUERY_PARAM = /([?&]sig=)[A-Za-z0-9%+/=]+/gi;
const MAX_DEPTH = 8;

/** Below the SDK's ChannelControllerPriority of 500, which would make this plugin a channel. */
const CORE_EXTENSION_PRIORITY = 400;

/**
 * Keeps the document access hash out of Application Insights. The hash is a read permission for the
 * invoice and it reaches telemetry in more fields than a field list can keep up with — the operation
 * name, page view urls, tracked request urls and exception messages all carry it. So does the
 * signature of the storage url the PDF is loaded from, a read permission for the PDF until it expires.
 *
 * Matches on the value rather than on the shape of a url, so a field nobody thought of still gets
 * redacted. The hash comes from the address of the page being measured, and hashes seen earlier in
 * the session are kept because an item can outlive the route it was created on.
 *
 * Attached through the SDK's own `config.extensions`, so the shared App Insights service stays
 * untouched. The priority runs it after the plugins that fill the operation name (110/120) and must
 * stay below `ChannelControllerPriority` (500) — at or above it the SDK treats the plugin as a
 * channel and calls `flush()` on it while unloading the page, which throws and kills that flush.
 */
export class DocumentHashRedactionPlugin extends BaseTelemetryPlugin {
  override identifier = 'DocumentHashRedaction';
  override priority = CORE_EXTENSION_PRIORITY;

  readonly #seenHashes = new Set<string>();

  processTelemetry(item: ITelemetryItem, itemCtx?: IProcessTelemetryContext): void {
    this.#rememberCurrentHash();
    redactSecretsInPlace(item, [...this.#seenHashes], 0);

    this.processNext(item, this._getTelCtx(itemCtx));
  }

  #rememberCurrentHash(): void {
    for (const hash of hashVariants(documentHashFromPath(globalThis.location?.pathname))) {
      this.#seenHashes.add(hash);
    }
  }
}

/** The hash is the detail segment of the document route; any other path carries none. */
function documentHashFromPath(pathname: string | undefined): string | null {
  const segments = (pathname ?? '').split('/');
  return segments.length >= 3 && DOCUMENT_ROUTE_SEGMENTS.has(segments[1]) && segments[2] !== '' ? segments[2] : null;
}

/** A hash travels raw in the path and may be encoded once it becomes a query parameter. */
function hashVariants(hash: string | null): string[] {
  if (hash === null) {
    return [];
  }
  const decoded = safeDecode(hash);
  return [...new Set([hash, decoded, encodeURIComponent(decoded)])];
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function redactSecretsInPlace(target: unknown, secrets: readonly string[], depth: number): void {
  if (depth > MAX_DEPTH || target === null || typeof target !== 'object') {
    return;
  }

  const record = target as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string') {
      const redacted = redactSecrets(value, secrets);
      if (redacted !== value) {
        record[key] = redacted;
      }
    } else {
      redactSecretsInPlace(value, secrets, depth + 1);
    }
  }
}

/** The query rule is a backstop for a hash that is not the one this page was opened with. */
function redactSecrets(value: string, secrets: readonly string[]): string {
  let result = value;
  for (const secret of secrets) {
    if (result.includes(secret)) {
      result = result.split(secret).join(REDACTED);
    }
  }
  return result.replace(HASH_QUERY_PARAM, `$1${REDACTED}`).replace(SAS_SIGNATURE_QUERY_PARAM, `$1${REDACTED_SIGNATURE}`);
}
